import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    setIsStandalone(isStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if already dismissed
    const dismissedStorage = localStorage.getItem("pwa-install-dismissed");
    if (dismissedStorage) {
      setDismissed(true);
      return;
    }

    // Android install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setDeferredPrompt(null);
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("pwa-install-dismissed", "true");
  };

  // Don't show if already installed, dismissed, or no prompt available
  if (isStandalone || dismissed || (!deferredPrompt && !isIOS)) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 z-50 shadow-lg md:left-auto md:right-4 md:w-96">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{t("pwa.title")}</CardTitle>
            <CardDescription className="mt-1">
              {isIOS ? t("pwa.descriptionIOS") : t("pwa.descriptionAndroid")}
            </CardDescription>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-2 text-muted-foreground hover:text-foreground"
            aria-label={t("pwa.close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isIOS ? (
          <div className="space-y-2 text-sm">
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>{t("pwa.step1")} <Share2 className="w-4 h-4 inline" /></li>
              <li>{t("pwa.step2")}</li>
              <li>{t("pwa.step3")}</li>
            </ol>
          </div>
        ) : (
          <Button onClick={handleInstall} className="w-full">
            <Download className="w-4 h-4 mr-2" />
            {t("pwa.install")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
